import React, { useRef } from 'react';
import { getContext } from '@fiverr-private/fiverr_context';
import { DEFAULT_FIVERR_LOCALE } from '@fiverr-private/localization';
import { Video as VideoComponent, useVideoBigQueryEvents } from '@fiverr-private/media';
import { useImpressionItem } from '@fiverr-private/impressionable';
import { Container, Section } from '@fiverr-private/layout_components';
import { I18n } from '@fiverr-private/i18n-react';
import { Typography } from '@fiverr-private/typography';
import SafeComponent from '../../shared/SafeComponent';
import { getImpressionEnrichment } from '../../utils/biEvents';
import { BQ_IMP_LOHP_VIDEO_IS_SHOWN } from '../../utils/biEvents/events/bigQueryEvents';
import { useIsMobileLayout } from '../../../../shared/hooks/useIsMobileLayout';
import { useVideoViewportPlayback } from '../../../../shared/hooks/useVideoViewportPlayback';
import { useVideoSourceReload } from '../../../../shared/hooks/useVideoSourceReload';
import { VIDEO_ASSETS, VIDEO_PLACEHOLEDER } from './config';

const Video = () => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const { locale } = getContext();
    const isMobileLayout = useIsMobileLayout(['default']);

    const videoSource = VIDEO_ASSETS[locale] || VIDEO_ASSETS[DEFAULT_FIVERR_LOCALE];
    const videoToRender = isMobileLayout ? videoSource?.mobile : videoSource?.desktop;

    const [ref] = useImpressionItem(getImpressionEnrichment({ eventName: BQ_IMP_LOHP_VIDEO_IS_SHOWN }));

    useVideoSourceReload(videoRef, videoToRender);
    useVideoViewportPlayback(videoRef, videoToRender);

    const BIMeta = {
        autoPlay: true,
        pageName: 'LOHP',
        videoName: 'How Fiverr Works',
    };

    useVideoBigQueryEvents(videoRef, BIMeta);

    return (
        <Section paddingTop={{ default: '8', md: '10' }} paddingBottom={{ default: '8', md: '10' }}>
            <Container data-test-id="fingertips-container" ref={ref}>
                <Typography
                    size={{ default: 'h_xl', md: 'h_xxl' }}
                    as="h2"
                    letterSpacing="-0.03em"
                    fontWeight="normal"
                    marginBottom={{ default: '2', md: '4' }}
                >
                    <I18n k="logged_out_homepage.video.title" />
                </Typography>

                <Typography color="grey_1000" fontWeight="normal" marginBottom={{ default: '6', md: '10' }}>
                    <I18n k="logged_out_homepage.video.description" />
                </Typography>

                <VideoComponent
                    ref={videoRef}
                    playsInline
                    poster={VIDEO_PLACEHOLEDER}
                    preload="auto"
                    muted
                    borderRadius="2xl"
                    height={{ md: '452px', xl: '589px' }}
                    sources={videoToRender ? [{ src: videoToRender, type: 'video/mp4' }] : []}
                    subtitles={videoSource?.subtitles}
                />
            </Container>
        </Section>
    );
};

export { Video };
export default SafeComponent(Video);
